import React, { useState, useEffect } from 'react';
import { Table, Card, Form, Row, Col, Badge, Alert, Spinner, Button } from 'react-bootstrap';
import { getHolidaysByYear, deleteHoliday } from '../../services/api';
import WorkingDaysCalculator from './WorkingDaysCalculator';
import HolidayAddModal from './HolidayAddModal';
import HolidayEditModal from './HolidayEditModal';

const HolidayList = () => {
    const [holidays, setHolidays] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [success, setSuccess] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);

    // Load holidays for selected year
    const loadHolidays = async (year) => {
        try {
            setLoading(true);
            setError('');

            const response = await getHolidaysByYear(year);
            if (response.success) {
                setHolidays(response.data.holidays || []);
            }
        } catch (error) {
            console.error('Load holidays error:', error);
            setError('Tatil listesi yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Handle add holiday success
    const handleAddSuccess = async (message) => {
        setSuccess(message);
        await loadHolidays(selectedYear);
        setTimeout(() => setSuccess(''), 3000);
    };
    // Handle edit holiday
    const handleEditHoliday = (holiday) => {
        setSelectedHoliday(holiday);
        setShowEditModal(true);
    };

    // Handle edit success
    const handleEditSuccess = async (message) => {
        setSuccess(message);
        await loadHolidays(selectedYear);
        setTimeout(() => setSuccess(''), 3000);
    };

    // Handle delete holiday
    const handleDeleteHoliday = async (holiday) => {
        if (!window.confirm(`"${holiday.name}" tatilini silmek istediğinizden emin misiniz?`)) {
            return;
        }

        try {
            setError('');
            const response = await deleteHoliday(holiday._id);
            if (response.success) {
                setSuccess('Tatil başarıyla silindi');
                await loadHolidays(selectedYear);
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Tatil silinirken hata oluştu');
        }
    };

    // Load holidays on component mount and year change
    useEffect(() => {
        loadHolidays(selectedYear);
    }, [selectedYear]);

    // Format Turkish date
    const formatTurkishDate = (date) => {
        return new Date(date).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get day name in Turkish
    const getDayName = (date) => {
        const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
        return days[new Date(date).getDay()];
    };

    // Get holiday type badge color
    const getTypeColor = (type) => {
        const colors = {
            'fixed': 'primary',
            'religious': 'success',
            'half_day': 'warning'
        };
        return colors[type] || 'secondary';
    };

    // Get holiday type display name
    const getTypeDisplayName = (type) => {
        const names = {
            'fixed': 'Sabit',
            'religious': 'Dini',
            'half_day': 'Yarım Gün'
        };
        return names[type] || type;
    };

    // Generate year options (current year ± 5)
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let year = currentYear - 2; year <= currentYear + 3; year++) {
        yearOptions.push(year);
    }

    return (
        <div className="holiday-list-component">
            {/* Header with Year Selector */}
            <Card className="mb-4">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <h5 className="mb-0">📅 RESMİ TATİLLER</h5>
                        </Col>
                        <Col md={6} className="text-end">
                            <Form.Select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                style={{ width: '150px', display: 'inline-block' }}
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </Form.Select>

                            <Button
                                variant="success"
                                size="sm"
                                className="ms-2"
                                onClick={() => setShowAddModal(true)}
                            >
                                + Tatil Ekle
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body>
                    {/* Error Message */}
                    {error && (
                        <Alert variant="danger">
                            {error}
                        </Alert>
                    )}

                    {/* Success Message */}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}

                    {/* Loading Spinner */}
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Tatiller yükleniyor...
                        </div>
                    ) : (
                        /* Holiday Table */
                        <Table striped hover responsive>
                                <thead className="table-dark">
                                    <tr>
                                        <th>Tarih</th>
                                        <th>Tatil Adı</th>
                                        <th>Tür</th>
                                        <th>Gün</th>
                                        <th width="120">İşlemler</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {holidays.length > 0 ? (
                                    holidays
                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                        .map((holiday, index) => (
                                            <tr key={holiday._id || index}>
                                                <td>
                                                    <strong>{formatTurkishDate(holiday.date)}</strong>
                                                </td>
                                                <td>{holiday.name}</td>
                                                <td>
                                                    <Badge bg={getTypeColor(holiday.type)}>
                                                        {getTypeDisplayName(holiday.type)}
                                                    </Badge>
                                                    {holiday.isHalfDay && (
                                                        <Badge bg="warning" className="ms-1">
                                                            Yarım Gün
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>{getDayName(holiday.date)}</td>
                                                <td>
                                                    {holiday._id && (
                                                        <div className="d-flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => handleEditHoliday(holiday)}
                                                            >
                                                                ✏️
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDeleteHoliday(holiday)}
                                                            >
                                                                🗑️
                                                            </Button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                                <td colSpan="5" className="text-center text-muted py-4">
                                            {selectedYear} yılı için tatil bilgisi bulunamadı.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}

                    {/* Summary */}
                    {holidays.length > 0 && (
                        <div className="mt-3">
                            <small className="text-muted">
                                📊 Toplam <strong>{holidays.length}</strong> resmi tatil günü
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Working Days Calculator */}
            <WorkingDaysCalculator />

            {/* Add Holiday Modal */}
            <HolidayAddModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSuccess={handleAddSuccess}
                selectedYear={selectedYear}
            />

            {/* Edit Holiday Modal */}
<HolidayEditModal
    show={showEditModal}
    onHide={() => {
        setShowEditModal(false);
        setSelectedHoliday(null);
    }}
    onSuccess={handleEditSuccess}
    holiday={selectedHoliday}
/>
        </div>
    );
};

export default HolidayList;